import { Badge, Box, Flex, HStack, VStack } from '@chakra-ui/react';
import { faPen, faRotateExclamation, faFilePlus } from '@fortawesome/pro-regular-svg-icons';
import { OfferTooltipLabels } from 'apply-data';
import { memo } from 'react';
import { RoundBadgeWithTooltip, getStatusByConditionType } from 'shared-components';
import { EApplicationPGKeys, EmailStatus } from 'api-paths-cas';
import { TableCellProps } from '../../../types';

import OfferStatusBadge from '@/components/molecules/offerStatusBadge';
import { defaultHeaderRenderer } from '../../headers';
import { IColumn } from '../../types';
import { UNKNOWN_VALUE } from '../helpers';
import { Tags } from '../TagComponent';

const Formatter = memo(function formatter(cellProps: TableCellProps) {
  const applicantStatus = getStatusByConditionType(cellProps?.row?.applicantStatus, cellProps?.row?.conditionType);
  return (
    <Flex height="100%" width="100%" flexDirection={'column'}>
      <VStack
        spacing="2"
        display="flex"
        alignItems="flex-start"
        justifyContent="flex-start"
        height="100%"
        py={4}
        paddingRight="12px"
      >
        <HStack justifyContent="space-between" width="100%">
          <Box fontWeight="medium" className="namesContainer" width="100%">
            <VStack align="start">
              <HStack width="100%" justifyContent="space-between">
                <Box fontSize="sm" color="muted">
                  {`${cellProps?.row?.studentNo ?? 'No student number'}`}
                </Box>

                <Box display="flex" columnGap="4px">
                  <OfferStatusBadge applicantStatus={applicantStatus} iconSize="xs" />

                  {cellProps?.row?.pendingChanges && (
                    <RoundBadgeWithTooltip
                      label={OfferTooltipLabels.pendingChanges}
                      icon={faPen}
                      colorScheme="orange"
                      size="xs"
                    />
                  )}

                  {Boolean(cellProps?.row?.newDocumentsCount) && (
                    <RoundBadgeWithTooltip
                      label={OfferTooltipLabels.newDocumentsCount(cellProps?.row?.newDocumentsCount)}
                      icon={faFilePlus}
                      counter={cellProps?.row?.newDocumentsCount}
                      colorScheme="indigo"
                      size="xs"
                    />
                  )}

                  {cellProps.row.potentialDuplicate && (
                    <RoundBadgeWithTooltip
                      label="Potential duplicates on your student management system"
                      colorScheme="red"
                      variant="solid"
                      size="xs"
                      icon={faRotateExclamation}
                    />
                  )}
                </Box>
              </HStack>

              <Box fontSize="medium">
                {`${cellProps?.row?.givenName ?? UNKNOWN_VALUE} ${cellProps?.row?.familyName ?? UNKNOWN_VALUE}`}
              </Box>
              {(!cellProps?.row?.email || cellProps?.row?.email === EmailStatus.NoEmail) && (
                <Badge size="xs" variant="solid" colorScheme="red">
                  Email unlinked
                </Badge>
              )}
            </VStack>
            <Box mt={3}></Box>
          </Box>
        </HStack>
        <Tags cellProps={cellProps} />
      </VStack>
    </Flex>
  );
});

export const StudentColumn = (): IColumn => ({
  item: {
    key: EApplicationPGKeys.FAMILY_NAME,
    name: 'Student',
    sortable: true,
    width: 'minmax(304px, 2.5fr)',
    frozen: true,
    formatter: Formatter,
    headerRenderer: defaultHeaderRenderer,
  },
  visible: () => true,
});
